import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath } from './onboardingRoutes';
import { AppInfoTooltip } from '../../components/AppInfoTooltip';
import { AppLogo } from './AppLogo';
import { AppsDisclosure, Chevron, COLLAPSE_APPS_ABOVE } from './AppsDisclosure';
import { OutcomesSummary } from './OutcomesSummary';
import { appBenefit } from './appBenefits';

interface PlanOutcome {
  code: string;
  displayName: string;
  description: string;
}

interface PlanApp {
  appId: string;
  displayName: string;
  /**
   * Phase 2.x-c PR-B — the pricing-summary endpoint returns the
   * mode-resolved Application Registry v2 copy directly. No client-side
   * join required.
   */
  subtitle?: string;
  description?: string;
  /**
   * Phase 2.x-c PR-C3b — outcome → app traceability. Every selected
   * outcome whose FOCUS_REGISTRY[outcome].apps array includes this
   * app. Used to render "Activated by: {outcomes}" beneath the chip so users
   * see which selection activated each app. Wire-optional so older
   * backends degrade to a flat list.
   */
  activatedByOutcomes?: string[];
  /** The app's logo — the same file the app switcher shows. */
  iconUrl?: string;
  order: number;
}

/**
 * Phase 2.x-c PR-C3a — the plan the user will actually be on after
 * completion. Populated by the same `computePlanForOutcomes` call
 * `completeOnboarding` runs, so what the pricing screen shows is what
 * the subscription writes.
 */
interface PlanCatalogEntry {
  code: string;
  name: string;
  description: string;
  /** T-7: null when the plan has no usable price — never treat as $0. */
  monthlyPriceCents: number | null;
  annualPriceCents: number | null;
  baseCredits: number;
  includedSeats: number;
  perSeatCents: number;
  maxSeats: number | null;
  storageLimitBytes: number;
}

interface PlanSummary {
  status: 'ready' | 'missing-outcomes';
  mode: 'business' | 'personal';
  workspaceName?: string;
  selectedOutcomes?: PlanOutcome[];
  /**
   * Apps completing onboarding WILL activate — the backend's canonical
   * prospective projection (never the workspace's current entitlements).
   */
  prospectiveApps?: PlanApp[];
  /** Deprecated alias of `prospectiveApps` (older backends send only this). */
  apps?: PlanApp[];
  pricing?: {
    /** T-7: null when no usable price exists. */
    monthlyBaseCents: number | null;
    totalCredits: number;
    trialDays: number;
    isFree: boolean;
    /**
     * T-7: false when the plan has no usable price. Shown as "Contact us"
     * (Billing PlanPicker wording) and the step cannot be continued — the
     * backend refuses completion with 409 PLAN_PRICE_UNAVAILABLE. Absent on
     * older backends; a null price is then treated the same way.
     */
    priceAvailable?: boolean;
    /** PR-C3a: plan the user will be on after completion. */
    plan?: PlanCatalogEntry;
    /** PR-C3a: plan.baseCredits + Σ outcome.includedCredits. */
    monthlyIncludedCredits?: number;
  };
}

/** Human-readable storage label from bytes. */
function formatStorage(bytes: number): string {
  if (bytes >= 1024 ** 4) return `${(bytes / 1024 ** 4).toFixed(bytes % (1024 ** 4) === 0 ? 0 : 1)} TB`;
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(bytes % (1024 ** 3) === 0 ? 0 : 1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
  return `${bytes} B`;
}

/** Human-readable dollar amount from integer cents (no trailing .00). */
function formatDollars(cents: number): string {
  return cents % 100 === 0 ? `$${(cents / 100).toFixed(0)}` : `$${(cents / 100).toFixed(2)}`;
}

/**
 * Phase 2.x-c PR-C3b — map outcome codes to their displayNames.
 * Returned array is de-duplicated and preserves the order the codes
 * appear in `activatedByOutcomes` (which itself follows the user's
 * outcome-selection order via getOnboardingPlanSummary). Codes with
 * no matching displayName are skipped — the user won't recognize
 * "revenue" as prose.
 */
function outcomeNamesFor(
  activatedByOutcomes: readonly string[],
  outcomeNameByCode: Map<string, string>,
): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const code of activatedByOutcomes) {
    const name = outcomeNameByCode.get(code);
    if (name && !seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  }
  return names;
}

type PageStatus = 'loading' | 'ready' | 'missing-outcomes' | 'error';

/** Capacity/activation-framed copy — pricing as what gets switched on. */
const MODE_COPY: Record<'business' | 'personal', { heading: string; sub: string }> = {
  business: {
    heading: 'Activate your business workspace',
    sub: 'Here is the operating system we assemble for your growth, operations, team, and clients.',
  },
  personal: {
    heading: 'Set up your personal space',
    sub: 'Here is everything we set up to help you organize your home, goals, and life.',
  },
};

/**
 * PricingSummary page — the onboarding pricing/activation step.
 *
 * Sources entirely from `GET /api/onboarding/pricing-summary`, which
 * resolves the plan from the workspace's STORED selectedOutcomes. When the
 * workspace has no usable outcomes the API returns `missing-outcomes` and
 * this page shows a recovery path to the outcomes step — it never renders a
 * fake "Free / 0 outcomes / 0 apps" summary.
 */
export function PricingSummaryPage() {
  const [status, setStatus] = useState<PageStatus>('loading');
  const [summary, setSummary] = useState<PlanSummary | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  // A long app list starts collapsed behind a one-line summary.
  const [appsExpanded, setAppsExpanded] = useState(false);
  // Phones: the trial benefits start folded away.
  const [trialOpen, setTrialOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await fetch('/api/onboarding/pricing-summary', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        setStatus('error');
        return;
      }
      setSummary(data as PlanSummary);
      setStatus(data.status === 'missing-outcomes' ? 'missing-outcomes' : 'ready');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const mode: 'business' | 'personal' = summary?.mode === 'personal' ? 'personal' : 'business';
  const isPersonal = mode === 'personal';

  const handleBack = async () => {
    try {
      await fetch('/api/onboarding/back', { method: 'POST', credentials: 'include' });
    } catch {
      // Ignore errors on back navigation
    }
    // Both flows place `outcomes` immediately before pricing (business R5,
    // personal R6) — Back always returns there.
    navigate(onboardingPath(mode, 'outcomes', continueUrl));
  };

  const handleContinue = async () => {
    setIsSaving(true);
    setError('');
    try {
      const response = await fetch('/api/onboarding/step', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ step: 'pricing', data: {} }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Failed to proceed');
        return;
      }
      // The backend is authoritative on what comes next. `completePricing`
      // applies the R4 conditional-review rule (skips review → invites when
      // there is nothing to review), so the page must navigate to the step
      // the response actually advances to. Hardcoding 'review' here was
      // what produced the pricing↔review bounce loop when reviewStepApplies
      // returned false.
      const backendStep = data?.state?.step as string | undefined;
      const nextStep = backendStep ?? (isPersonal ? 'complete' : 'review');
      navigate(onboardingPath(mode, nextStep, continueUrl));
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (status === 'loading') {
    return (
      <OnboardingLayout currentStep="pricing" onboardingMode={mode} wide>
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Preparing your plan...</p>
        </div>
      </OnboardingLayout>
    );
  }

  // The workspace has no usable outcomes — recover to the outcomes step
  // rather than show a fake 0-outcome / 0-app plan.
  if (status === 'missing-outcomes') {
    return (
      <OnboardingLayout currentStep="pricing" onboardingMode={mode} wide>
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 text-center max-w-md mx-auto">
          <h1 className="text-lg font-semibold text-gray-900">
            Let's choose your outcomes first
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            We need to know what you want this workspace to help with before we
            can set it up. It only takes a moment.
          </p>
          <button
            type="button"
            onClick={() => navigate(onboardingPath(mode, 'outcomes', continueUrl))}
            className="mt-6 px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-600/25"
          >
            Choose your outcomes
          </button>
        </div>
      </OnboardingLayout>
    );
  }

  if (status === 'error') {
    return (
      <OnboardingLayout currentStep="pricing" onboardingMode={mode} wide>
        <div className="bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5 rounded-2xl p-8 text-center max-w-md mx-auto">
          <h1 className="text-lg font-semibold text-gray-900">Something went wrong</h1>
          <p className="text-sm text-gray-500 mt-2">
            We couldn't prepare your plan. Your selections are safe — please try again.
          </p>
          <button
            type="button"
            onClick={() => load()}
            className="mt-6 px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-600/25"
          >
            Try again
          </button>
        </div>
      </OnboardingLayout>
    );
  }

  // status === 'ready' — summary is populated with real outcomes/apps.
  const selectedOutcomes = summary?.selectedOutcomes ?? [];
  // Backend-owned prospective projection — what completion will activate.
  const apps = summary?.prospectiveApps ?? summary?.apps ?? [];
  // PR-C3b — outcome-code → displayName map used to render the
  // "Activated by: {outcomes}" traceability line on each app chip. Built inline
  // (no useMemo) since the outcome list is at most a dozen entries.
  const outcomeNameByCode = new Map<string, string>(
    selectedOutcomes.map((o) => [o.code, o.displayName] as const),
  );
  const pricing = summary?.pricing ?? {
    monthlyBaseCents: 0,
    totalCredits: 0,
    trialDays: 0,
    isFree: true,
    plan: undefined,
    monthlyIncludedCredits: undefined,
  };
  // PR-C3a: plan lookup is the single source of truth for what the
  // user will actually be billed. When present, all downstream copy
  // pulls from it; when absent (legacy backend), we degrade to the
  // outcome-driven wording from earlier phases without lying.
  const plan = pricing.plan;
  const monthlyIncludedCredits =
    pricing.monthlyIncludedCredits ?? pricing.totalCredits;
  // T-7: a missing / invalid price is never "Free" or "$0". Only an explicit
  // number counts as a price; `priceAvailable === false` always wins.
  const priceUnavailable =
    pricing.priceAvailable === false ||
    (plan ? typeof plan.monthlyPriceCents !== 'number' : typeof pricing.monthlyBaseCents !== 'number');
  // The backend refuses to complete onboarding onto a resolved plan that has
  // no price (PLAN_PRICE_UNAVAILABLE), so the step cannot be continued.
  const continueBlocked = !!plan && priceUnavailable;
  const planPriceCents =
    plan && !priceUnavailable && typeof plan.monthlyPriceCents === 'number' ? plan.monthlyPriceCents : null;
  const baseCents =
    !priceUnavailable && typeof pricing.monthlyBaseCents === 'number' ? pricing.monthlyBaseCents : null;
  // No trial is promised for a plan that cannot be completed.
  const showTrial = pricing.trialDays > 0 && !continueBlocked;

  const SectionTitle = ({ title, count }: { title: string; count?: number }) => (
    <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 sm:mb-3 sm:text-xs">
      {typeof count === 'number' ? `${title} (${count})` : title}
    </h3>
  );

  return (
    <OnboardingLayout currentStep="pricing" onboardingMode={mode} wide>
      <div className="rounded-3xl bg-white shadow-2xl shadow-black/40 ring-1 ring-black/5">
        <div className="grid lg:grid-cols-12">
          {/*
            PR-C3a — the price/plan hero. The dollar figure comes straight from
            `plan.monthlyPriceCents`, so what the user sees is exactly what
            completeOnboarding writes to `WorkspaceSubscription`. When no plan came
            back on the wire we degrade to a plain heading, never a made-up number.
          */}
          <aside
            className="relative overflow-hidden rounded-t-3xl lg:rounded-l-3xl lg:rounded-tr-none bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900 p-4 sm:p-8 text-white lg:col-span-4"
            data-testid="pricing-hero"
          >
            <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
            <div className="relative">
              {/* Phones: workspace name and trial badge share one line. */}
              <div className="flex flex-wrap items-center justify-between gap-2 lg:block">
                <p className="min-w-0 truncate text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-300 sm:text-xs">
                  {summary?.workspaceName || 'Your workspace'}
                </p>
                {showTrial && (
                  <span className="inline-flex flex-none items-center rounded-full bg-emerald-400/15 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-200 ring-1 ring-emerald-300/30 sm:px-3 sm:py-1 sm:text-xs lg:mt-3">
                    {pricing.trialDays}-day free trial
                  </span>
                )}
              </div>
              {plan ? (
                <>
                  <p className="mt-3 text-[11px] uppercase tracking-[0.16em] text-slate-400 sm:mt-6 sm:text-xs">Your plan</p>
                  {/* Phones: plan name and price side by side. */}
                  <div className="flex items-end justify-between gap-3 lg:block">
                  <p className="mt-0.5 text-2xl font-semibold tracking-tight sm:mt-1 sm:text-4xl">
                    <span data-testid="pricing-plan-name">{plan.name}</span>
                  </p>
                  <div className="text-right text-slate-200 lg:mt-3 lg:text-left">
                    {planPriceCents === null ? (
                      // T-7: no usable price — PlanPicker wording, never "$0".
                      <span className="text-xl font-semibold sm:text-2xl" data-testid="pricing-plan-price">Contact us</span>
                    ) : planPriceCents === 0 ? (
                      <span className="text-xl font-semibold sm:text-2xl" data-testid="pricing-plan-price">
                        Free · {formatDollars(0)}/mo
                      </span>
                    ) : (
                      <>
                        <span className="text-2xl font-semibold text-white sm:text-4xl" data-testid="pricing-plan-price">
                          {formatDollars(planPriceCents)}/mo
                        </span>
                        {pricing.trialDays > 0 && (
                          <span className="mt-0.5 block text-[11px] text-slate-300 sm:mt-1 sm:text-sm">
                            after your {pricing.trialDays}-day free trial
                          </span>
                        )}
                        {/* No "or $X/year": Central checkout sells MONTHLY only. */}
                      </>
                    )}
                  </div>
                  </div>
                  <p className="mt-2.5 text-xs leading-relaxed text-slate-300 sm:mt-4 sm:text-sm">
                    {isPersonal
                      ? `We picked ${plan.name} for the ${selectedOutcomes.length} ${
                          selectedOutcomes.length === 1 ? 'area' : 'areas'
                        } you chose.`
                      : `We picked ${plan.name} — our starting business tier — because it covers every outcome you selected.`}
                  </p>
                  {isPersonal && typeof planPriceCents === 'number' && planPriceCents > 0 && (
                    <p
                      className="mt-4 rounded-xl border border-white/15 bg-white/10 p-3 text-sm leading-relaxed text-emerald-50"
                      data-testid="pricing-personal-paid-note"
                    >
                      You'll add payment after setup to activate {plan.name}. Until then your space
                      runs on Free — nothing is charged without your confirmation.
                    </p>
                  )}
                  {continueBlocked && (
                    <p
                      className="mt-4 rounded-xl border border-amber-300/30 bg-amber-400/15 p-3 text-sm leading-relaxed text-amber-100"
                      data-testid="pricing-price-unavailable"
                      role="status"
                    >
                      This plan is not available for online sign-up yet. Contact us to set it
                      up, or go back and choose different outcomes.
                    </p>
                  )}
                </>
              ) : (
                <div className="mt-6">
                  {/* Defensive fallback when the backend returns no plan sub-object. */}
                  <p className="text-3xl font-semibold" data-testid="pricing-plan-price">
                    {baseCents === null
                      ? 'Pricing unavailable'
                      : pricing.isFree
                        ? 'Free'
                        : `${formatDollars(baseCents)}/mo`}
                  </p>
                  {pricing.trialDays > 0 && (
                    <p className="mt-1 text-sm text-slate-300">after your {pricing.trialDays}-day free trial</p>
                  )}
                </div>
              )}

              {showTrial && (
                <div className="mt-3 border-t border-white/10 pt-3 sm:mt-6 sm:pt-5">
                  <p className="hidden text-sm font-semibold text-white lg:block">Your {pricing.trialDays}-day free trial</p>
                  {/* Phones: the benefits fold away behind their heading. */}
                  <button
                    type="button"
                    onClick={() => setTrialOpen((v) => !v)}
                    aria-expanded={trialOpen}
                    aria-controls="pricing-trial-benefits"
                    className="flex w-full items-center justify-between gap-2 text-left text-sm font-semibold text-white lg:hidden"
                  >
                    What's included in your {pricing.trialDays}-day trial
                    <Chevron expanded={trialOpen} className="text-emerald-300" />
                  </button>
                  <ul
                    id="pricing-trial-benefits"
                    className={`${trialOpen ? 'block' : 'hidden'} mt-3 space-y-2 text-sm text-slate-300 lg:block`}
                  >
                    {/* Not "trial credits": Central has no separate trial-credit grant;
                        a trial is funded with the plan's own monthly allocation. */}
                    {[
                      'Full access to every outcome you selected',
                      "Your plan's monthly credits during the trial",
                      'No credit card required',
                      'Cancel anytime',
                    ].map((line) => (
                      <li key={line} className="flex gap-2">
                        <svg className="mt-0.5 h-4 w-4 flex-none text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </aside>

          <section className="min-w-0 p-4 sm:p-8 lg:col-span-8">
            <div className="mb-4 sm:mb-6">
              <h1 className="text-xl sm:text-3xl font-semibold tracking-tight text-gray-900">{MODE_COPY[mode].heading}</h1>
              <p className="mt-1 text-sm text-gray-500 sm:mt-1.5 sm:text-base">{MODE_COPY[mode].sub}</p>
            </div>

            {/* Selected outcomes. Per-outcome $ badges were retired (outcomes carry no price). */}
            <div className="mb-5 sm:mb-7">
              <SectionTitle title="What you're improving" count={selectedOutcomes.length} />
              <OutcomesSummary outcomes={selectedOutcomes} listId="pricing-outcomes" />
            </div>

            {/*
              Apps we'll activate — "activate", not "Included": apps come from
              outcomes, not from the plan. Each name is paired with its plain
              benefit, so an app is never just a product name. PR-C3b
              traceability: with more than one outcome, each app names the
              selection(s) that activated it.
            */}
            <div className="mb-5 sm:mb-7">
              <SectionTitle title="Apps we'll activate" count={apps.length} />
              {apps.length > COLLAPSE_APPS_ABOVE && (
                <AppsDisclosure
                  apps={apps}
                  expanded={appsExpanded}
                  onToggle={() => setAppsExpanded((v) => !v)}
                  controlsId="pricing-apps"
                />
              )}
              <div
                id="pricing-apps"
                hidden={apps.length > COLLAPSE_APPS_ABOVE && !appsExpanded}
                className={`${apps.length > COLLAPSE_APPS_ABOVE && !appsExpanded ? 'hidden' : 'grid'} gap-2.5 sm:grid-cols-2 xl:grid-cols-3 ${
                  apps.length > COLLAPSE_APPS_ABOVE ? 'mt-3' : ''
                }`}
              >
                {apps.map((app) => {
                  const traceNames =
                    selectedOutcomes.length > 1 && app.activatedByOutcomes
                      ? outcomeNamesFor(app.activatedByOutcomes, outcomeNameByCode)
                      : null;
                  const purpose = appBenefit(app, mode);
                  return (
                    <div
                      key={app.appId}
                      className="flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition-shadow hover:shadow-md"
                    >
                      <AppLogo appId={app.appId} displayName={app.displayName} iconUrl={app.iconUrl} />
                      <AppInfoTooltip
                        description={app.description}
                        className={`block min-w-0 text-left leading-tight ${
                          app.description ? 'rounded focus:outline-none focus:ring-2 focus:ring-emerald-400' : ''
                        }`}
                        testId={`pricing-app-${app.appId}`}
                      >
                        <span className="block text-sm font-semibold text-gray-900">{app.displayName}</span>
                        {purpose && (
                          <span className="mt-0.5 block text-xs text-gray-500">{purpose}</span>
                        )}
                        {traceNames && traceNames.length > 0 && (
                          <span
                            className="mt-1 block text-[11px] leading-snug text-gray-400 line-clamp-2"
                            data-testid={`pricing-app-trace-${app.appId}`}
                            title={traceNames.join(', ')}
                          >
                            Activated by: {traceNames.join(', ')}
                          </span>
                        )}
                      </AppInfoTooltip>
                    </div>
                  );
                })}
              </div>
            </div>

            {/*
              Capacity included in your plan — "included" describes plan capacity
              only, never apps. Plan credits and outcome credits are shown apart.
            */}
            {plan && (
              <div data-testid="pricing-capacity">
                <SectionTitle title="Capacity included in your plan" />
                <dl className="grid grid-cols-3 gap-2 sm:gap-2.5">
                  <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 sm:p-4">
                    <dt className="text-[11px] font-medium text-gray-500 sm:text-xs">Monthly credits</dt>
                    <dd className="mt-1 text-gray-900">
                      <span data-testid="pricing-capacity-credits" className="text-lg font-semibold sm:text-2xl">
                        {monthlyIncludedCredits.toLocaleString()}
                      </span>
                    </dd>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 sm:p-4">
                    <dt className="text-[11px] font-medium text-gray-500 sm:text-xs">Seats</dt>
                    <dd className="mt-1 text-gray-900">
                      <span data-testid="pricing-capacity-seats" className="text-lg font-semibold sm:text-2xl">
                        {plan.includedSeats}
                      </span>
                    </dd>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-3 sm:p-4">
                    <dt className="text-[11px] font-medium text-gray-500 sm:text-xs">Storage</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900 sm:text-2xl" data-testid="pricing-capacity-storage">
                      {formatStorage(plan.storageLimitBytes)}
                    </dd>
                  </div>
                </dl>
                {(pricing.totalCredits > 0 || (plan.perSeatCents > 0 && plan.maxSeats !== null)) && (
                  <p className="mt-2 text-xs leading-relaxed text-gray-400">
                    {pricing.totalCredits > 0 && (
                      <span className="block">
                        Credits: {plan.baseCredits.toLocaleString()} from your plan
                        {' + '}
                        {pricing.totalCredits.toLocaleString()} from your outcomes
                      </span>
                    )}
                    {plan.perSeatCents > 0 && plan.maxSeats !== null && (
                      <span className="block">
                        Seats: add more for {formatDollars(plan.perSeatCents)}/seat/mo
                        {plan.maxSeats > plan.includedSeats && <> up to {plan.maxSeats}</>}
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}

            {error && (
              <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </section>
        </div>

        <div className="sticky bottom-0 z-10 flex flex-row items-center justify-between gap-3 rounded-b-3xl border-t border-gray-100 bg-white px-3 sm:px-8 py-2 sm:py-4">
          <button
            type="button"
            onClick={handleBack}
            className="px-4 sm:px-6 py-2.5 text-gray-600 hover:text-gray-900 transition-colors font-medium"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={isSaving || continueBlocked}
            className="px-5 py-2.5 sm:px-7 sm:py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-emerald-600/25"
          >
            {isSaving ? 'Continuing...' : 'Continue'}
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
